/**
 * VitalsForm.jsx
 *
 * Entry form for resident vital signs
 * GraphQL-safe, schema-aligned, production hardened
 */

import React, { useState } from "react";
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
} from "@mui/material";
import { gql, useMutation } from "@apollo/client";

const SAVE_VITALS = gql`
  mutation SaveVital($data: VitalsInput!) {
    saveVital(data: $data) {
      id
      bp
      pulse
      oxygen
      temperature
      respiration
      weight
      createdAt
    }
  }
`;

export default function VitalsForm({ residentId, onSave }) {
    const [form, setForm] = useState({
        bp: "",
        pulse: "",
        oxygen: "",
        temperature: "",
        respiration: "",
        weight: "",
    });

    const [saveVital, { loading }] = useMutation(SAVE_VITALS);

    const handleChange = (field) => (e) => {
        setForm((prev) => ({
            ...prev,
            [field]: e.target.value,
        }));
    };

    /**
     * Build GraphQL-safe payload
     * Matches VitalsInput exactly
     */
    const buildPayload = () => {
        if (!residentId) {
            throw new Error("residentId is required to save vitals");
        }

        return {
            residentId,

            // Blood pressure stays STRING
            bp: form.bp.trim() || null,

            // Integer fields
            pulse: form.pulse !== "" ? Number(form.pulse) : null,
            oxygen: form.oxygen !== "" ? Number(form.oxygen) : null,
            respiration: form.respiration !== "" ? Number(form.respiration) : null,

            // Float fields
            temperature:
                form.temperature !== "" ? Number(form.temperature) : null,
            weight: form.weight !== "" ? Number(form.weight) : null,
        };
    };

    const submit = async () => {
        try {
            const payload = buildPayload();

            // 🔍 Debug once, remove later if you want
            console.log("Saving vitals payload:", payload);

            const res = await saveVital({
                variables: { data: payload },
                update(cache, { data }) {
                    const newVital = data?.saveVital;
                    if (!newVital) return;

                    cache.modify({
                        fields: {
                            getVitals(existing = []) {
                                return [newVital, ...existing];
                            },
                        },
                    });
                },
            });


            onSave?.(res.data.saveVital);

            // Reset form
            setForm({
                bp: "",
                pulse: "",
                oxygen: "",
                temperature: "",
                respiration: "",
                weight: "",
            });
        } catch (err) {
            console.error("Failed to save vitals:", err);
            alert(err.message);
        }
    };

    const vitalFields = [
        {
            key: "bp",
            label: "Blood Pressure",
            placeholder: "120/80 mmHg",
            type: "text",
        },
        {
            key: "pulse",
            label: "Pulse",
            placeholder: "72 bpm",
            type: "number",
        },
        {
            key: "oxygen",
            label: "Oxygen Saturation (SpO₂)",
            placeholder: "95 %",
            type: "number",
        },
        {
            key: "temperature",
            label: "Temperature",
            placeholder: "98.6 °F",
            type: "number",
            step: "0.1",
        },
        {
            key: "respiration",
            label: "Respiration",
            placeholder: "16 /min",
            type: "number",
        },
        {
            key: "weight",
            label: "Weight",
            placeholder: "150 lb",
            type: "number",
            step: "0.1",
        },
    ];


    return (
        <Box>
            <Typography variant="h6" mb={2}>
                Record Vitals
            </Typography>

            <Grid container spacing={2}>
                {vitalFields.map(({ key, label, placeholder, type, step }) => (
                    <Grid item xs={6} key={key}>
                        <TextField
                            fullWidth
                            label={label}
                            placeholder={placeholder}
                            value={form[key]}
                            onChange={handleChange(key)}
                            type={type}
                            InputLabelProps={{
                                shrink: true, // ✅ REQUIRED
                            }}
                            inputProps={{ step }}
                        />
                    </Grid>
                ))}

                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        onClick={submit}
                        disabled={loading || !residentId}
                    >
                        Save Vitals
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
}
